import useTranslator from "../../hooks/useTranslator";
import moment from "moment";
import styles from "./LandlordMissingDocNoteBox.module.css";
import { LANDLORD_MISSING_DOC_NOTE_BOX_ID } from "./LandlordNotePillButton";
import { ViewNoteIcon } from "./ViewMissingDocNoteButton";

// Read-only box showing the tenant's missing-documents explanation note at
// the end of Documentos on landlord views (report + landlord profile).
// Never edits, never marks documents as received. Props:
// - note: the TenantMissingDocNote response (explainedDocuments, message,
//   origin signup|profile, updated_at), or null (renders nothing).
// - onDocumentClick: called with the raw document key when the landlord
//   clicks a document entry, scrolling back to that row. Keys are paired
//   to rows by canonical key, so every vocabulary lands on the same row.
// - translateDocName: maps a document key to its display label.
const LandlordMissingDocNoteBox = ({
  note = null,
  onDocumentClick,
  translateDocName,
}) => {
  const { _t } = useTranslator();
  if (!note) return null;

  const displayName = (docKey) =>
    typeof translateDocName === "function" ? translateDocName(docKey) : docKey;

  return (
    <div className={styles.noteBox} id={LANDLORD_MISSING_DOC_NOTE_BOX_ID}>
      <p className={styles.noteMeta}>
        <span className={styles.noteIcon}>
          <ViewNoteIcon />
        </span>
        <strong>{_t("missingDocNoteTitle", "Tenant note")}</strong>
        <span>
          {" "}
          ·{" "}
          {note?.updated_at
            ? moment(note.updated_at).format("DD-MM-YYYY")
            : null}{" "}
          ·{" "}
          {note?.origin === "signup"
            ? _t("missingDocNoteAtSignup", "at sign-up")
            : _t("missingDocNoteInProfile", "in the profile")}{" "}
          ·{" "}
          {_t(
            "missingDocNoteNotReceived",
            "Does not mark any document as received"
          )}
        </span>
      </p>
      <p className={styles.noteMessage}>
        &ldquo;{note?.explanationMessage ?? ""}&rdquo;
      </p>
      {(note?.explainedDocuments ?? []).length > 0 && (
        <p className={styles.noteAbout}>
          {_t("missingDocNoteAbout", "About")}:{" "}
          {(note.explainedDocuments ?? []).map((doc, index) => {
            const rawKey = doc?.documentKey ?? "";
            return (
              <span key={`${rawKey}-${index}`}>
                {index > 0 && ", "}
                <button
                  type="button"
                  className={styles.noteDocLink}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (typeof onDocumentClick === "function") {
                      onDocumentClick(rawKey, event.currentTarget);
                    }
                  }}
                >
                  {displayName(rawKey)}
                </button>
              </span>
            );
          })}
        </p>
      )}
    </div>
  );
};

export default LandlordMissingDocNoteBox;
